export interface CalendarEventInput {
  title: string;
  description?: string;
  location?: string;
  startDateTime: string;
  endDateTime?: string;
}

// Event start/end times are stored and displayed as local wall-clock time (no timezone info
// travels with them anywhere else in the app), so we treat the value's date/time components
// as-is rather than reinterpreting them through the browser's timezone.
const formatForCalendar = (dateString: string): string => {
  const d = new Date(dateString);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
};

const fallbackEnd = (startDateTime: string, endDateTime?: string): string => {
  if (endDateTime) return endDateTime;
  const d = new Date(startDateTime);
  d.setHours(d.getHours() + 1);
  return d.toISOString();
};

export const buildGoogleCalendarUrl = (event: CalendarEventInput): string => {
  const end = fallbackEnd(event.startDateTime, event.endDateTime);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${formatForCalendar(event.startDateTime)}/${formatForCalendar(end)}`,
    ctz: 'America/New_York',
  });
  if (event.description) params.set('details', event.description);
  if (event.location) params.set('location', event.location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

const escapeIcsText = (value: string): string =>
  value.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\r?\n/g, '\\n');

export const buildIcsFileContent = (event: CalendarEventInput): string => {
  const end = fallbackEnd(event.startDateTime, event.endDateTime);
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Church of God Cincinnati//Announcements//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${Date.now()}-${Math.random().toString(36).slice(2)}@cogcincinnati.org`,
    `DTSTAMP:${formatForCalendar(new Date().toISOString())}Z`,
    `DTSTART;TZID=America/New_York:${formatForCalendar(event.startDateTime)}`,
    `DTEND;TZID=America/New_York:${formatForCalendar(end)}`,
    `SUMMARY:${escapeIcsText(event.title)}`,
  ];
  if (event.description) lines.push(`DESCRIPTION:${escapeIcsText(event.description)}`);
  if (event.location) lines.push(`LOCATION:${escapeIcsText(event.location)}`);
  lines.push('END:VEVENT', 'END:VCALENDAR');
  return lines.join('\r\n');
};

export const downloadIcsFile = (event: CalendarEventInput): void => {
  const content = buildIcsFileContent(event);
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'event'}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const buildGoogleMapsDirectionsUrl = (location: string): string =>
  `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(location)}`;
